import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ErrorBoundary from '../../../molecules/ErrorBoundary'
import LineChart from '../../../charts/echarts/LineChart'
import { planning } from '../../../../api/integrations'
import ChartProvider from '../../../charts/echarts/ChartProvider'

function AreaChart ({
  queryType,
  payload,
  seriesSelector,
  dataSelector,
  dataOverrides,
  categorySelector,
  categories,
  chartOptions,
  ...seriesProps
}) {
  const { data, isLoading } = planning.usePlanningQuery({ queryType, payload })

  const chartData = useMemo(() => {
    if (isLoading || !data) return []

    return Object.entries(get(data, seriesSelector, {})).reduce((acc, [key, val]) => {
      acc.push({
        seriesId: key,
        name: dataOverrides?.[key]?.name ?? key,
        data: dataSelector ? get(val, dataSelector) : val,
        stack: 'Total',
        areaStyle: dataOverrides?.[key]?.areaStyle ?? {},
        ...(dataOverrides?.[key] ?? {})
      })

      return acc
    }, [])
  }, [dataOverrides, dataSelector, data, isLoading, seriesSelector])

  const xAxis = useMemo(() => seriesProps?.xAxis ?? ({
    type: 'category',
    data: categories ?? get(data, categorySelector, [])
  }), [data, seriesProps, categorySelector, categories])

  const yAxis = useMemo(() => seriesProps?.yAxis ?? ({
    type: 'value'
  }), [seriesProps])

  const chartContext = useMemo(() => ({
    ...(AreaChart.defaultProps.chartOptions),
    ...chartOptions,
    xAxis,
    yAxis
  }), [xAxis, yAxis, chartOptions])

  if (isLoading) return null

  return (
    <ErrorBoundary name='Area Chart'>
      <ChartProvider {...chartContext}>
        {chartData.map(({ seriesId, data, ...seriesProps }) => {
          return (<LineChart key={seriesId} seriesId={seriesId} data={data} {...seriesProps} />)
        })}
      </ChartProvider>
    </ErrorBoundary>
  )
}

AreaChart.propTypes = {
  queryType: PropTypes.string,
  payload: PropTypes.object,
  categories: PropTypes.array,
  dataOverrides: PropTypes.object,
  dataSelector: PropTypes.string,
  categorySelector: PropTypes.string,
  seriesSelector: PropTypes.string,
  // clientId: PropTypes.number,
  // planType: PropTypes.oneOf(['proposed', 'current']),
  chartOptions: PropTypes.object
}

AreaChart.defaultProps = {
  chartOptions: {
    width: 1000,
    height: 600,
    title: {
      text: ''
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        label: {
          backgroundColor: '#6a7985'
        }
      }
    },
    legend: {
      padding: 20,
      data: ['Current', 'Recommended']
    },
    toolbox: {
      feature: {
        saveImageAs: {}
      }
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3$',
      containLabel: true
    }
  }
}

export default AreaChart
